import {
  Injectable,
  signal,
  computed,
  inject,
  PLATFORM_ID,
  effect,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { catchError, map } from 'rxjs/operators';
import type {
  AddItemToCartPayload,
  AddItemToCartPayloadBackend,
  Cart,
  CartItem,
  UpdateCartItemPayload,
} from '../types/Cart.types';
import {
  CartDetalleEstado,
  isCartItemEnCarrito,
  isCartItemGuardadoParaDespues,
} from '../types/Cart.types';
import { EmailValidationService } from './email-validation.service';
import { AuthStateService } from './auth-state.service';
import { environment } from '../environments/environment';
import { ApiMembresiaResponse } from '../types/API.types';
import { CartStatus } from '../pages/cart/carrito.component';
import { BehaviorSubject, firstValueFrom, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  LOCAL_STORAGE_KEY_AUTH_TOKEN,
  LOCAL_STORAGE_SAVED_FOR_LATER,
} from '../config/variables';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  private readonly STORAGE_KEY = 'allas-cart';

  private platformId = inject(PLATFORM_ID);
  private http = inject(HttpClient);
  // Señales reactivas del carrito
  private cart = signal<Cart>(this.getInitialCart());
  private currentCartId: number | null = null;
  private emailValidationService = inject(EmailValidationService);
  private authStateService = inject(AuthStateService);

  // Observable para suscribirse a cambios del carrito
  cart$ = new BehaviorSubject<Cart>(this.getInitialCart());

  // Computed values para la UI (solo líneas activas en carrito)
  cartItems = computed(() => {
    const cart = this.cart();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    return items.filter(isCartItemEnCarrito);
  });
  savedForLaterItems = computed(() => {
    const cart = this.cart();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    return items.filter(isCartItemGuardadoParaDespues);
  });
  totalItems = computed(() => {
    return this.cartItems().reduce(
      (total, item) => total + (item?.cantidad || 0),
      0
    );
  });
  isEmpty = computed(() => this.cartItems().length === 0);

  /**
   * Obtiene el observable del carrito para suscribirse a cambios
   * @returns Observable<Cart>
   */
  getCartObservable() {
    return this.cart$.asObservable();
  }

  constructor() {
    // Verificar si hay sesión al inicializar
    this.initializeCart();
    this.setupAuthStateListener();
  }

  private setupAuthStateListener(): void {
    this.authStateService.authState$.subscribe((authChange) => {
      if (authChange.type === 'login') {
        void this.onLogin();
      } else if (authChange.type === 'logout') {
        this.clearCartFromStorage();
      }
    });
  }

  /**
   * Tras login: migra carrito guest + "guardar para después" al backend y carga el carrito.
   */
  private async onLogin(): Promise<void> {
    try {
      const guestCartItems = this.snapshotGuestCartItems();
      await this.migrateGuestCartItems(guestCartItems);
      await this.migrateGuestSavedForLater();
      await this.setCartFromBackend();
    } catch (error) {
      console.error('Error sincronizando carrito tras login:', error);
    }
  }

  /**
   * Snapshot de líneas activas del carrito guest (memoria o `allas-cart`).
   * No usa carritos que ya parecen del backend (usuario autenticado).
   */
  private snapshotGuestCartItems(): CartItem[] {
    const memoryCart = this.cart();
    if (this.isGuestLocalCart(memoryCart)) {
      const items = Array.isArray(memoryCart.items) ? memoryCart.items : [];
      return items.filter(isCartItemEnCarrito);
    }

    const stored = this.readStoredCart();
    if (stored && this.isGuestLocalCart(stored)) {
      const items = Array.isArray(stored.items) ? stored.items : [];
      return items.filter(isCartItemEnCarrito);
    }

    return [];
  }

  private isGuestLocalCart(cart: Cart | null | undefined): boolean {
    if (!cart) {
      return false;
    }
    const idUsuario = Number(cart.idUsuarioDigital ?? 0);
    const id = Number(cart.id ?? -1);
    return idUsuario === 0 || id < 0 || id === -1;
  }

  private readStoredCart(): Cart | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (!raw) {
        return null;
      }
      return JSON.parse(raw) as Cart;
    } catch (error) {
      console.error('Error leyendo carrito local:', error);
      return null;
    }
  }

  /**
   * Sube ítems del carrito guest activo al backend (`idEstado = 1`).
   * Best-effort por ítem.
   */
  async migrateGuestCartItems(items: CartItem[]): Promise<void> {
    if (!this.isUserLoggedInAndHasCart()) {
      return;
    }

    if (!Array.isArray(items) || items.length === 0) {
      return;
    }

    for (const item of items) {
      const payload = this.buildBackendPayloadFromGuestItem(
        item,
        CartDetalleEstado.EN_CARRITO
      );
      if (!payload) {
        console.warn(
          'Omitiendo ítem de carrito guest sin SKU ni grupo al migrar:',
          item
        );
        continue;
      }

      try {
        await firstValueFrom(this.addItemToCartInBackend(payload));
      } catch (error) {
        console.error(
          'Error migrando ítem de carrito guest al backend:',
          payload,
          error
        );
      }
    }
  }

  private buildBackendPayloadFromGuestItem(
    item: CartItem,
    idEstado: number
  ): AddItemToCartPayloadBackend | null {
    const codigoProducto =
      (item.codigoProducto ?? item.producto?.sku)?.trim() || '';
    const idGrupo = item.idGrupo ?? item.grupo?.id;

    if (!codigoProducto && (idGrupo == null || idGrupo === 0)) {
      return null;
    }

    const payload: AddItemToCartPayloadBackend = {
      cantidad: item.cantidad > 0 ? item.cantidad : 1,
      idEstado,
      notas: item.notas ?? undefined,
    };

    if (codigoProducto) {
      payload.codigoProducto = codigoProducto;
    } else if (idGrupo != null) {
      payload.idGrupo = idGrupo;
    }

    return payload;
  }

  /**
   * Sube ítems guest de `allas_saved_for_later` al backend (`idEstado = 2`).
   * Best-effort por ítem; al final limpia la clave local.
   */
  async migrateGuestSavedForLater(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!this.isUserLoggedInAndHasCart()) {
      return;
    }

    let raw: string | null = null;
    try {
      raw = localStorage.getItem(LOCAL_STORAGE_SAVED_FOR_LATER);
    } catch (error) {
      console.error('Error leyendo guardados para después locales:', error);
      return;
    }

    if (!raw) {
      return;
    }

    let items: CartItem[] = [];
    try {
      const parsed = JSON.parse(raw);
      items = Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Error parseando guardados para después locales:', error);
      this.clearGuestSavedForLaterStorage();
      return;
    }

    for (const item of items) {
      const payload = this.buildBackendPayloadFromGuestItem(
        item,
        CartDetalleEstado.GUARDADO_PARA_DESPUES
      );
      if (!payload) {
        console.warn(
          'Omitiendo ítem guardado sin SKU ni grupo al migrar:',
          item
        );
        continue;
      }

      try {
        await firstValueFrom(this.addItemToCartInBackend(payload));
      } catch (error) {
        console.error(
          'Error migrando ítem guardado para después al backend:',
          payload,
          error
        );
      }
    }

    this.clearGuestSavedForLaterStorage();
  }

  private clearGuestSavedForLaterStorage(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    try {
      localStorage.removeItem(LOCAL_STORAGE_SAVED_FOR_LATER);
    } catch (error) {
      console.error('Error limpiando guardados para después locales:', error);
    }
  }

  async setCartFromBackend(_idUsuarioDigital?: number) {
    const cart = await firstValueFrom(this.getCartFromBackend());

    this.setCart(cart);
  }

  setCart(cart: Cart) {
    // Asegurar que items siempre sea un array
    const validCart = {
      ...cart,
      items: Array.isArray(cart?.items) ? cart.items : [],
    };
    this.cart.set(validCart);
    this.saveCartToStorage(validCart);
    this.currentCartId = validCart.id;
    this.cart$.next(validCart);
  }

  getCartFromBackend() {
    const url = `${environment.apiUrl}/v1/carrito`;

    return this.http
      .get<ApiMembresiaResponse<Cart>>(url, { headers: this.getAuthHeaders() })
      .pipe(
        map((response) => {
          if (!response.data)
            throw new Error('Error obteniendo carrito del backend');

          return response.data;
        }),
        catchError((error) =>
          this.handleCartHttpError(error, 'Error obteniendo carrito del backend')
        )
      );
  }

  private getAuthToken(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    return localStorage.getItem(LOCAL_STORAGE_KEY_AUTH_TOKEN);
  }

  private getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
      Expires: '0',
    };

    if (!isPlatformBrowser(this.platformId)) {
      return headers;
    }

    const token = this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Loguea el status HTTP; en 401/403 limpia sesión zombie.
   * Re-lanza un Error con el status cuando está disponible.
   */
  private handleCartHttpError(error: unknown, fallbackMessage: string) {
    const status =
      error instanceof HttpErrorResponse
        ? error.status
        : error && typeof error === 'object' && 'status' in error
          ? Number((error as { status: unknown }).status)
          : undefined;

    if (status != null && Number.isFinite(status)) {
      console.error(`${fallbackMessage} (HTTP ${status})`, error);
    } else {
      console.error(fallbackMessage, error);
    }

    if (status === 401 || status === 403) {
      this.emailValidationService.logout();
    }

    const message =
      status != null && Number.isFinite(status)
        ? `${fallbackMessage} (HTTP ${status})`
        : fallbackMessage;
    return throwError(() => new Error(message));
  }

  addItemToCartInBackend(payload: AddItemToCartPayloadBackend) {
    // El API crea/resuelve el carrito activo por JWT; no exige idCarrito local.
    const url = `${environment.apiUrl}/v1/carrito/detalle`;

    return this.http
      .post<ApiMembresiaResponse<Cart>>(url, payload, {
        headers: this.getAuthHeaders(),
      })
      .pipe(
        map((response) => {
          if (!response.data)
            throw new Error('Error agregando producto al carrito en backend');

          this.setCart(response.data);

          return response.data;
        }),
        catchError((error) =>
          this.handleCartHttpError(
            error,
            'Error agregando producto al carrito en backend'
          )
        )
      );
  }

  removeProductFromCartInBackend(idDetalle: number) {
    if (!this.currentCartId) {
      throw new Error('No hay carrito activo en el backend');
    }

    const url = `${environment.apiUrl}/v1/carrito/detalle/${idDetalle}`;

    // Lean response: success confirms deletion; local cart is updated by the caller.
    return this.http
      .delete<ApiMembresiaResponse<{ id: number }>>(url, {
        headers: this.getAuthHeaders(),
      })
      .pipe(
        map((response) => {
          if (!response.success) {
            throw new Error('Error eliminando producto del carrito en backend');
          }
        }),
        catchError((error) =>
          this.handleCartHttpError(
            error,
            'Error eliminando producto del carrito en backend'
          )
        )
      );
  }

  updateItemInBackend(updatePayload: UpdateCartItemPayload) {
    if (!this.currentCartId) {
      throw new Error('No hay carrito activo en el backend');
    }

    const url = `${environment.apiUrl}/v1/carrito/detalle/${updatePayload.idDetalle}`;

    // Lean response: success confirms update; local cart is updated by the caller.
    return this.http
      .put<ApiMembresiaResponse<{ id: number }>>(url, updatePayload, {
        headers: this.getAuthHeaders(),
      })
      .pipe(
        map((response) => {
          if (!response.success) {
            throw new Error('Error actualizando producto en backend');
          }
        }),
        catchError((error) =>
          this.handleCartHttpError(
            error,
            'Error actualizando producto en backend'
          )
        )
      );
  }

  /**
   * Cambia el estado de una línea (en carrito / guardado para después).
   * Recarga el carrito completo tras el PATCH.
   */
  updateDetalleEstado(idDetalle: number, idEstado: number) {
    const url = `${environment.apiUrl}/v1/carrito/detalle/${idDetalle}/estado`;

    return this.http
      .patch<ApiMembresiaResponse<{ id: number; idEstado: number }>>(
        url,
        { idEstado },
        { headers: this.getAuthHeaders() }
      )
      .pipe(
        map((response) => {
          if (!response.success) {
            throw new Error('Error actualizando estado del detalle');
          }
          return response.data;
        }),
        catchError((error) =>
          this.handleCartHttpError(
            error,
            'Error actualizando estado del detalle'
          )
        )
      );
  }

  async saveForLater(idDetalle: number): Promise<void> {
    await firstValueFrom(
      this.updateDetalleEstado(idDetalle, CartDetalleEstado.GUARDADO_PARA_DESPUES)
    );
    await this.setCartFromBackend();
  }

  async moveToCart(idDetalle: number): Promise<void> {
    await firstValueFrom(
      this.updateDetalleEstado(idDetalle, CartDetalleEstado.EN_CARRITO)
    );
    await this.setCartFromBackend();
  }

  async updateCartStatus(
    cartId: string,
    status: CartStatus,
    nombreContacto: string,
    telefonoContacto: string
  ) {
    try {
      const url = `${environment.apiUrl}/v1/carrito/${cartId}/estado`;
      const body = {
        idEstado: status,
        nombreContacto: nombreContacto,
        telefonoContacto: telefonoContacto,
      };

      await firstValueFrom(
        this.http
          .patch(url, body, { headers: this.getAuthHeaders() })
          .pipe(
            catchError((error) =>
              this.handleCartHttpError(
                error,
                'Error al actualizar el estado del carrito'
              )
            )
          )
      );
    } catch (error) {
      console.error('Error al actualizar el estado del carrito:', error);
    }
  }

  saveCartToStorage(cart: Cart) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cart));
    } catch (error) {
      throw new Error('Error saving cart to storage');
    }
  }

  clearCartFromStorage() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    localStorage.removeItem(this.STORAGE_KEY);
    this.clearCart();
  }

  isUserLoggedIn() {
    const userData = this.emailValidationService.getCurrentUser();
    return !!userData;
  }

  /**
   * Usuario digital + token presentes. Si hay usuario sin token, limpia sesión.
   */
  isUserLoggedInAndHasCart() {
    const userData = this.emailValidationService.getCurrentUser();
    if (!userData?.idUsuarioDigital) {
      return false;
    }

    if (!this.getAuthToken()) {
      this.emailValidationService.logout();
      return false;
    }

    return true;
  }

  getCurrentUser() {
    return this.emailValidationService.getCurrentUser();
  }

  initializeCart(): void {
    if (this.isUserLoggedInAndHasCart()) {
      void (async () => {
        try {
          await this.migrateGuestSavedForLater();
          await this.setCartFromBackend();
        } catch (error) {
          console.error('Error inicializando carrito desde backend:', error);
        }
      })();
    } else {
      // Si no hay sesión válida, cargar desde localStorage
      this.getCartFromStorage();
    }
  }

  getCurrentCartId() {
    return this.currentCartId;
  }

  private getCartFromStorage() {
    if (!isPlatformBrowser(this.platformId)) {
      const initialCart = this.getInitialCart();
      this.setCart(initialCart);
      return initialCart;
    }

    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) {
        const initialCart = this.getInitialCart();
        this.setCart(initialCart);
        return initialCart;
      }
      const cart = JSON.parse(stored) as Cart;
      this.setCart(cart);
      return cart;
    } catch (error) {
      const initialCart = this.getInitialCart();
      this.setCart(initialCart);
      return initialCart;
    }
  }

  /**
   * Agrega un item al carrito
   */
  async addItemToCart(payload: AddItemToCartPayload) {
    const cart = this.cart();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const existingItem = items.find(
      (item) =>
        isCartItemEnCarrito(item) &&
        ((item.idGrupo && item.idGrupo === payload.categoria?.id) ||
          (item.codigoProducto &&
            item.codigoProducto === payload.producto?.sku))
    );

    if (existingItem) {
      await this.updateItemInCart({
        ...existingItem,
        cantidad: existingItem.cantidad + (payload.cantidad || 1),
      });

      return;
    }

    if (this.isUserLoggedInAndHasCart()) {
      await firstValueFrom(
        this.addItemToCartInBackend({
          codigoProducto: payload.producto?.sku || '',
          idGrupo: payload.categoria?.id || 0,
          cantidad: payload.cantidad || 1,
        })
      );

      return;
    }

    const currentCart = this.cart();
    const currentItems = Array.isArray(currentCart?.items)
      ? currentCart.items
      : [];

    const newItem: CartItem = {
      id: currentItems.length + 1,
      idCarrito: this.currentCartId || 0,
      cantidad: 1,
      fechaCreacion: new Date(),
      idEstado: CartDetalleEstado.EN_CARRITO,
      producto: payload.producto,
      grupo: payload.categoria,
      idGrupo: payload.categoria?.id,
      notas: '',
    };

    this.setCart({
      ...currentCart,
      items: [...currentItems, newItem],
    });
  }

  /**
   * Actualiza un item en el carrito
   */
  async updateItemInCart(payload: CartItem) {
    const cart = structuredClone(this.cart());

    if (!cart || !Array.isArray(cart.items)) {
      return;
    }

    const item = cart.items.find((cartItem) => cartItem.id === payload.id);
    if (!item) return;

    if (this.isUserLoggedInAndHasCart()) {
      await firstValueFrom(
        this.updateItemInBackend({
          idDetalle: payload.id,
          cantidad: payload.cantidad,
          notas: payload.notas,
        })
      );
    }

    item.cantidad = payload.cantidad;
    item.notas = payload.notas;
    this.setCart(cart);
  }

  async updateItemInCartFromId(
    idDetalle: number,
    cantidad: number,
    notas?: string
  ) {
    const cart = this.cart();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const item = items.find((item) => item.id === idDetalle);

    if (!item) return;

    await this.updateItemInCart({
      ...item,
      cantidad: cantidad,
      notas: notas,
    });
  }

  /**
   * Elimina un item del carrito
   */
  async removeItemFromCart(id: number) {
    const cart = structuredClone(this.cart());

    if (!cart || !Array.isArray(cart.items)) {
      return;
    }

    if (this.isUserLoggedInAndHasCart()) {
      await firstValueFrom(this.removeProductFromCartInBackend(id));
    }

    cart.items = cart.items.filter((item) => item.id !== id);
    this.setCart(cart);
  }

  isItemInCart(idDetalle: number): boolean {
    return this.cartItems().some((item) => item.id === idDetalle);
  }

  getQuantityInCart(idDetalle: number): number {
    return this.cartItems().find((item) => item.id === idDetalle)?.cantidad || 0;
  }

  /**
   * Limpia todo el carrito
   */
  clearCart(): void {
    this.setCart(this.getInitialCart());

    // Limpiar también el carrito del backend
    this.currentCartId = null;
  }

  /**
   * Obtiene el carrito inicial sin modificar el signal
   */
  private getInitialCart(): Cart {
    return {
      id: -1,
      idUsuarioDigital: 0,
      idVehiculoSeleccionado: null,
      sucursalSeleccionada: null,
      nombreContacto: null,
      telefonoContacto: null,
      fechaCreacion: new Date(),
      idEstado: 1,
      items: [],
    };
  }

  getDefaultVehicle() {
    return this.cart().idVehiculoSeleccionado;
  }
}
